import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    navigation: {
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      signOut: 'Sign Out',
      home: 'Home',
      about: 'About',
    },
    home: {
      welcomeTitle: 'Welcome to Polyglots Academy',
      welcomeSubtitle: 'Empowering Global Talent Through Training and Assessment',
      getStarted: 'Get Started',
      learnMore: 'Learn More',
      forCandidates: 'For Candidates',
      forCandidatesDesc: 'Access comprehensive training programs, skill assessments, and career opportunities',
      trainings: 'Trainings',
      trainingsDesc: 'Industry-relevant courses designed by experts',
      assessments: 'Assessments',
      assessmentsDesc: 'Validate your skills with our assessment platform',
      placements: 'Placements',
      placementsDesc: 'Get connected with top employers',
      forEmployers: 'For Employers',
      forEmployersDesc: 'Find pre-trained, assessed talent ready to contribute from day one',
      preScreened: 'Pre-screened Talent',
      preScreenedDesc: 'Access candidates who have passed our rigorous assessments',
      customTraining: 'Custom Training',
      customTrainingDesc: 'Tailor training programs to your specific needs',
      quickHiring: 'Quick Hiring',
      quickHiringDesc: 'Reduce time-to-hire with our streamlined process',
    },
    stats: {
      trainedCandidates: 'Trained Candidates',
      partnerCompanies: 'Partner Companies',
      placementRate: 'Placement Rate',
      countries: 'Countries',
    },
    dashboard: {
      dashboard: 'Dashboard',
      profile: 'Profile',
      myTrainings: 'My Trainings',
      myAssessments: 'My Assessments',
      myBatches: 'My Batches',
      myStudents: 'My Students',
      welcome: 'Welcome',
      talentPools: 'Talent Pools',
      notifications: 'Notifications',
      noNotifications: 'No notifications yet',
      markAllRead: 'Mark all as read',
      myProfile: 'My Profile',
      resetPassword: 'Reset Password',
      accountInfo: 'Account Info',
      mainMenu: 'Main Menu',
      myRequests: 'My Requests',
      communication: 'Communication',
      feedbackSupport: 'Feedback & Support',
      account: 'Account',
      settings: 'Settings',
      messages: 'Messages',
    },
    common: {
      email: 'Email',
      password: 'Password',
      name: 'Name',
      role: 'Role',
      submit: 'Submit',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      search: 'Search',
      filter: 'Filter',
    },
    auth: {
      loginTitle: 'Login to Your Account',
      registerTitle: 'Create Your Account',
      dontHaveAccount: "Don't have an account?",
      alreadyHaveAccount: 'Already have an account?',
      signUp: 'Sign up',
      signIn: 'Sign in',
    },
    roles: {
      candidate: 'Candidate',
      teacher: 'Teacher',
      employer: 'Employer',
      admin: 'Admin',
      staff: 'Staff',
      recruitingPartner: 'Employer',
    },
    adminDashboard: {
      title: 'Admin Dashboard',
      subtitle: 'Full System Access',
      searchUsers: 'Search users...',
      createUser: 'Create User',
      allUsers: 'All Users',
      pendingApprovals: 'Pending Approvals',
      staffMembers: 'Staff Members',
      recruitingPartners: 'Employers',
      candidates: 'Candidates',
      noUsersFound: 'No users found',
      user: 'User',
      approvalStatus: 'Approval Status',
      profileStatus: 'Profile Status',
      registered: 'Registered',
      actions: 'Actions',
      approve: 'Approve',
      reject: 'Reject',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      complete: 'Complete',
      incomplete: 'Incomplete',
      createNewUser: 'Create New User',
      userDetails: 'User Details',
      close: 'Close',
    },
    staffDashboard: {
      title: 'Staff Dashboard',
      subtitle: 'User Approval & Management',
      selectStatus: 'Select Status',
      allStatus: 'All Status',
      selectRole: 'Select Role',
      allRoles: 'All Roles',
    },
    recruitingDashboard: {
      title: 'Employer Dashboard',
      subtitle: 'Find Your Next Hire',
      searchCandidates: 'Search candidates...',
      sortBy: 'Sort By',
      nameAZ: 'Name (A-Z)',
      nameZA: 'Name (Z-A)',
      experienceHighLow: 'Experience (High-Low)',
      experienceLowHigh: 'Experience (Low-High)',
      candidatesFound: 'Candidates Found',
      showInterest: 'Show Interest',
      interestShown: 'Interest Shown',
      downloadResume: 'Download Resume',
      viewProfile: 'View Profile',
      candidateDetails: 'Candidate Details',
      experience: 'Experience',
      qualification: 'Qualification',
      germanLevel: 'German Level',
      contactInformation: 'Contact Information',
      showInterestInCandidate: 'Show Interest in This Candidate',
      interestAlreadyShown: 'Interest Already Shown',
      welcomeBack: 'Welcome Back',
      enterpriseSubtitle: 'Strategic Talent Acquisition & Pipeline Intelligence'
    },
    notifications: {
      title: 'Notifications',
      noNotifications: 'No notifications yet',
      markAsRead: 'Mark as read',
      refresh: 'Refresh',
    },
    actions: {
      viewDetails: 'View Details',
    },
    partner: {
      legalEntityName: 'Legal Entity Name',
      tradeName: 'Trade Name (DBA)',
      companyWebsite: 'Company Website',
      yearOfEstablishment: 'Year of Establishment',
      companySize: 'Company Size',
      specialization: 'Specialization/Industry',
      accountManagerName: 'Account Manager Name',
      officialEmail: 'Official Email',
      directPhone: 'Direct Phone',
      taxId: 'Tax Identification Number (TIN/VAT)',
      incorporationCertificate: 'Certificate of Incorporation',
      laborLicense: 'Labor License',
      msmeCertificate: 'MSME Diversity Certificate',
      logo: 'Company Logo',
      editProfile: 'Edit Company Profile',
      pointOfContact: 'Point of Contact',
      complianceDocuments: 'Compliance Documents'
    }
  },
  de: {
    navigation: {
      login: 'Anmelden',
      register: 'Registrieren',
      logout: 'Abmelden',
      signOut: 'Abmelden',
      home: 'Startseite',
      about: 'Über uns',
    },
    home: {
      welcomeTitle: 'Willkommen bei Polyglots Academy',
      welcomeSubtitle: 'Stärkung globaler Talente durch Schulung und Bewertung',
      getStarted: 'Loslegen',
      learnMore: 'Mehr erfahren',
      forCandidates: 'Für Kandidaten',
      forCandidatesDesc: 'Zugang zu umfassenden Schulungsprogrammen, Kompetenzbewertungen und Karrieremöglichkeiten',
      trainings: 'Schulungen',
      trainingsDesc: 'Branchenrelevante Kurse von Experten entwickelt',
      assessments: 'Bewertungen',
      assessmentsDesc: 'Validieren Sie Ihre Fähigkeiten mit unserer Bewertungsplattform',
      placements: 'Vermittlungen',
      placementsDesc: 'Verbinden Sie sich mit Top-Arbeitgebern',
      forEmployers: 'Für Arbeitgeber',
      forEmployersDesc: 'Finden Sie vorgebildete, bewertete Talente, die vom ersten Tag an beitragen können',
      preScreened: 'Vorgeprüfte Talente',
      preScreenedDesc: 'Zugang zu Kandidaten, die unsere strengen Bewertungen bestanden haben',
      customTraining: 'Maßgeschneiderte Schulung',
      customTrainingDesc: 'Passen Sie Schulungsprogramme an Ihre spezifischen Bedürfnisse an',
      quickHiring: 'Schnelle Einstellung',
      quickHiringDesc: 'Reduzieren Sie die Einstellungszeit mit unserem optimierten Prozess',
    },
    stats: {
      trainedCandidates: 'Geschulte Kandidaten',
      partnerCompanies: 'Partnerunternehmen',
      placementRate: 'Vermittlungsquote',
      countries: 'Länder',
    },
    dashboard: {
      dashboard: 'Dashboard',
      profile: 'Profil',
      myTrainings: 'Meine Schulungen',
      myAssessments: 'Meine Bewertungen',
      myBatches: 'Meine Gruppen',
      myStudents: 'Meine Studenten',
      welcome: 'Willkommen',
      talentPools: 'Talent Pools',
      notifications: 'Benachrichtigungen',
      noNotifications: 'Noch keine Benachrichtigungen',
      markAllRead: 'Alle als gelesen markieren',
      myProfile: 'Mein Profil',
      resetPassword: 'Passwort zurücksetzen',
      accountInfo: 'Kontoinformationen',
      mainMenu: 'Hauptmenü',
      myRequests: 'Meine Anfragen',
      communication: 'Kommunikation',
      feedbackSupport: 'Feedback & Support',
      account: 'Konto',
      settings: 'Einstellungen',
      messages: 'Nachrichten',
    },
    common: {
      email: 'E-Mail',
      password: 'Passwort',
      name: 'Name',
      role: 'Rolle',
      submit: 'Absenden',
      cancel: 'Abbrechen',
      save: 'Speichern',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      search: 'Suchen',
      filter: 'Filtern',
    },
    auth: {
      loginTitle: 'Bei Ihrem Konto anmelden',
      registerTitle: 'Ihr Konto erstellen',
      dontHaveAccount: 'Noch kein Konto?',
      alreadyHaveAccount: 'Bereits ein Konto?',
      signUp: 'Registrieren',
      signIn: 'Anmelden',
    },
    roles: {
      candidate: 'Kandidat',
      teacher: 'Lehrer',
      employer: 'Arbeitgeber',
      admin: 'Administrator',
      staff: 'Mitarbeiter',
      recruitingPartner: 'Arbeitgeber',
    },
    adminDashboard: {
      title: 'Admin-Dashboard',
      subtitle: 'Vollständiger Systemzugriff',
      searchUsers: 'Benutzer suchen...',
      createUser: 'Benutzer erstellen',
      allUsers: 'Alle Benutzer',
      pendingApprovals: 'Ausstehende Genehmigungen',
      staffMembers: 'Mitarbeiter',
      recruitingPartners: 'Arbeitgeber',
      candidates: 'Kandidaten',
      noUsersFound: 'Keine Benutzer gefunden',
      user: 'Benutzer',
      approvalStatus: 'Genehmigungsstatus',
      profileStatus: 'Profilstatus',
      registered: 'Registriert',
      actions: 'Aktionen',
      approve: 'Genehmigen',
      reject: 'Ablehnen',
      pending: 'Ausstehend',
      approved: 'Genehmigt',
      rejected: 'Abgelehnt',
      complete: 'Vollständig',
      incomplete: 'Unvollständig',
      createNewUser: 'Neuen Benutzer erstellen',
      userDetails: 'Benutzerdetails',
      close: 'Schließen',
    },
    staffDashboard: {
      title: 'Mitarbeiter-Dashboard',
      subtitle: 'Benutzergenehmigung & Verwaltung',
      selectStatus: 'Status auswählen',
      allStatus: 'Alle Status',
      selectRole: 'Rolle auswählen',
      allRoles: 'Alle Rollen',
    },
    recruitingDashboard: {
      title: 'Arbeitgeber-Dashboard',
      subtitle: 'Finden Sie Ihre nächste Einstellung',
      searchCandidates: 'Kandidaten suchen...',
      sortBy: 'Sortieren nach',
      nameAZ: 'Name (A-Z)',
      nameZA: 'Name (Z-A)',
      experienceHighLow: 'Erfahrung (Hoch-Niedrig)',
      experienceLowHigh: 'Erfahrung (Niedrig-Hoch)',
      candidatesFound: 'Kandidaten gefunden',
      showInterest: 'Interesse zeigen',
      interestShown: 'Interesse gezeigt',
      downloadResume: 'Lebenslauf herunterladen',
      viewProfile: 'Profil anzeigen',
      candidateDetails: 'Kandidatendetails',
      experience: 'Erfahrung',
      qualification: 'Qualifikation',
      germanLevel: 'Deutschkenntnisse',
      contactInformation: 'Kontaktinformationen',
      showInterestInCandidate: 'Interesse an diesem Kandidaten zeigen',
      interestAlreadyShown: 'Interesse bereits gezeigt',
      welcomeBack: 'Willkommen zurück',
      enterpriseSubtitle: 'Strategische Talentakquise & Pipeline-Intelligenz'
    },
    notifications: {
      title: 'Benachrichtigungen',
      noNotifications: 'Noch keine Benachrichtigungen',
      markAsRead: 'Als gelesen markieren',
      refresh: 'Aktualisieren',
    },
    actions: {
      viewDetails: 'Details anzeigen',
    },
    partner: {
      legalEntityName: 'Name der juristischen Person',
      tradeName: 'Handelsname (DBA)',
      companyWebsite: 'Unternehmens-Website',
      yearOfEstablishment: 'Gründungsjahr',
      companySize: 'Unternehmensgröße',
      specialization: 'Spezialisierung/Branche',
      accountManagerName: 'Name des Kundenbetreuers',
      officialEmail: 'Offizielle E-Mail',
      directPhone: 'Direkte Telefonnummer',
      taxId: 'Steuernummer (USt-IdNr./St.-Nr.)',
      incorporationCertificate: 'Handelsregisterauszug',
      laborLicense: 'Gewerbeerlaubnis',
      msmeCertificate: 'MSME-Diversitätszertifikat',
      logo: 'Unternehmenslogo',
      editProfile: 'Unternehmensprofil bearbeiten',
      pointOfContact: 'Ansprechpartner',
      complianceDocuments: 'Compliance-Dokumente'
    }
  },
  it: {
    navigation: {
      login: 'Accedi',
      register: 'Registrati',
      logout: 'Esci',
      signOut: 'Disconnetti',
      home: 'Home',
      about: 'Chi siamo',
    },
    home: {
      welcomeTitle: 'Benvenuto a Polyglots Academy',
      welcomeSubtitle: 'Potenziare i Talenti Globali attraverso la Formazione e la Valutazione',
      getStarted: 'Inizia',
      learnMore: 'Scopri di più',
      forCandidates: 'Per i Candidati',
      forCandidatesDesc: 'Accedi a programmi di formazione, valutazioni delle competenze e opportunità di carriera',
      trainings: 'Corsi',
      trainingsDesc: "Corsi rilevanti per l'industria progettati da esperti",
      assessments: 'Valutazioni',
      assessmentsDesc: 'Valida le tue competenze con la nostra piattaforma',
      placements: 'Collocamenti',
      placementsDesc: 'Connettiti con i migliori datori di lavoro',
      forEmployers: 'Per i Datori di Lavoro',
      forEmployersDesc: 'Trova talenti pre-formati pronti a contribuire dal primo giorno',
      preScreened: 'Talenti Pre-selezionati',
      preScreenedDesc: 'Accedi a candidati che hanno superato le nostre rigorose valutazioni',
      customTraining: 'Formazione Personalizzata',
      customTrainingDesc: 'Adatta i programmi di formazione alle tue esigenze',
      quickHiring: 'Assunzioni Rapide',
      quickHiringDesc: 'Riduci il tempo di assunzione con il nostro processo semplificato',
    },
    stats: {
      trainedCandidates: 'Candidati Formati',
      partnerCompanies: 'Aziende Partner',
      placementRate: 'Tasso di Collocamento',
      countries: 'Paesi',
    },
    dashboard: {
      dashboard: 'Dashboard',
      profile: 'Profilo',
      myTrainings: 'I Miei Corsi',
      myAssessments: 'Le Mie Valutazioni',
      myBatches: 'I Miei Gruppi',
      myStudents: 'I Miei Studenti',
      welcome: 'Benvenuto',
      talentPools: 'Talent Pools',
      notifications: 'Notifiche',
      noNotifications: 'Nessuna notifica',
      markAllRead: 'Segna tutti come letti',
      myProfile: 'Il Mio Profilo',
      resetPassword: 'Reimposta Password',
      accountInfo: 'Informazioni Account',
      mainMenu: 'Menu Principale',
      myRequests: 'Le Mie Richieste',
      communication: 'Comunicazione',
      feedbackSupport: 'Feedback e Supporto',
      account: 'Account',
      settings: 'Impostazioni',
      messages: 'Messaggi',
    },
    common: {
      email: 'Email',
      password: 'Password',
      name: 'Nome',
      role: 'Ruolo',
      submit: 'Invia',
      cancel: 'Annulla',
      save: 'Salva',
      delete: 'Elimina',
      edit: 'Modifica',
      search: 'Cerca',
      filter: 'Filtra',
    },
    auth: {
      loginTitle: 'Accedi al tuo Account',
      registerTitle: 'Crea il tuo Account',
      dontHaveAccount: 'Non hai un account?',
      alreadyHaveAccount: 'Hai già un account?',
      signUp: 'Registrati',
      signIn: 'Accedi',
    },
    roles: {
      candidate: 'Candidato',
      teacher: 'Insegnante',
      employer: 'Datore di Lavoro',
      admin: 'Amministratore',
      staff: 'Personale',
      recruitingPartner: 'Datore di Lavoro',
    },
    adminDashboard: {
      title: 'Dashboard Admin',
      subtitle: 'Accesso Completo al Sistema',
      searchUsers: 'Cerca utenti...',
      createUser: 'Crea Utente',
      allUsers: 'Tutti gli Utenti',
      pendingApprovals: 'Approvazioni in Attesa',
      staffMembers: 'Membri dello Staff',
      recruitingPartners: 'Datori di Lavoro',
      candidates: 'Candidati',
      noUsersFound: 'Nessun utente trovato',
      user: 'Utente',
      approvalStatus: 'Stato Approvazione',
      profileStatus: 'Stato Profilo',
      registered: 'Registrato',
      actions: 'Azioni',
      approve: 'Approva',
      reject: "Rifiuta",
      pending: "In Attesa",
      approved: "Approvato",
      rejected: "Rifiutato",
      complete: "Completo",
      incomplete: "Incompleto",
      createNewUser: "Crea Nuovo Utente",
      userDetails: "Dettagli Utente",
      close: "Chiudi",
    },
    staffDashboard: {
      title: 'Dashboard Staff',
      subtitle: 'Gestione Approvazioni',
      selectStatus: 'Seleziona Stato',
      allStatus: 'Tutti gli Stati',
      selectRole: 'Seleziona Ruolo',
      allRoles: 'Tutti i Ruoli',
    },
    recruitingDashboard: {
      title: 'Dashboard Datore di Lavoro',
      subtitle: 'Trova il tuo prossimo dipendente',
      searchCandidates: 'Cerca candidati...',
      sortBy: 'Ordina per',
      nameAZ: 'Nome (A-Z)',
      nameZA: 'Nome (Z-A)',
      experienceHighLow: 'Esperienza (Alta-Bassa)',
      experienceLowHigh: 'Esperienza (Bassa-Alta)',
      candidatesFound: 'Candidati Trovati',
      showInterest: 'Mostra Interesse',
      interestShown: 'Interesse Mostrato',
      downloadResume: 'Scarica Curriculum',
      viewProfile: 'Vedi Profilo',
      candidateDetails: 'Dettagli Candidato',
      experience: 'Esperienza',
      qualification: 'Qualificazione',
      germanLevel: 'Livello di Tedesco',
      contactInformation: 'Informazioni di Contatto',
      showInterestInCandidate: 'Mostra Interesse per questo Candidato',
      interestAlreadyShown: 'Interesse Già Mostrato',
      welcomeBack: 'Bentornato',
      enterpriseSubtitle: 'Acquisizione Strategica di Talenti e Pipeline Intelligence'
    },
    notifications: {
      title: 'Notifiche',
      noNotifications: 'Nessuna notifica',
      markAsRead: 'Segna come letto',
      refresh: 'Ricarica',
    },
    actions: {
      viewDetails: 'Vedi Dettagli',
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    if (!key) return '';
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        return translations[language][key] || key; // Fallback to flat key or literal
      }
    }

    return typeof value === 'string' ? value : (translations[language][key] || key);
  };

  const switchLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
